// STARWEAVE — Spike A shell + Spike B planetary render.
// Renders the deterministic planet (7,42,3) from the shared WASM core as a
// cube-sphere with quadtree LOD, atmosphere, and an orbit/approach/surface
// camera. Keeps the Spike-A bridge, capability detection, and HUD.
//   ?phase=orbit|approach|surface  pins the camera (used for evidence).
//   default: a slow orbit->surface->orbit fly.

import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { loadWorldgen } from './worldgen';
import { sendToNative, onNativeMessage, bridgeAvailable } from './bridge';
import { createMultiplayer } from './net';
import { assembleShip } from './ship';
import { createFlight } from './flight';
import type { Body } from './starsystem';
import { buildWorld, disposeWorld, type WorldTextures } from './world';
import { setHud, bootDone } from './ui';
import { audio } from './audio';
import { createDiscovery } from './discovery';
import { init as initOnboarding } from './onboarding';
import * as juice from './juice';

const UNIVERSE = 0xa1b2c3d4e5f60718n;
function seedHash(x: number): number {
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
  return (x ^ (x >>> 16)) >>> 0;
}
// Persistent per-player identity → a unique random spawn + ship, stable per browser.
function playerSeed(): number {
  let s = localStorage.getItem('sw_player');
  if (!s) { s = String((Math.random() * 0x7fffffff) | 0); localStorage.setItem('sw_player', s); }
  return parseInt(s, 10) | 0;
}
function querySpawn(): { g: number; s: number; b: number; ps: number } {
  const ps = playerSeed();
  const p = new URLSearchParams(location.search).get('planet');
  if (p) {
    const [g, s, b] = p.split(',').map(Number);
    if ([g, s, b].every(Number.isFinite)) return { g, s, b, ps };
  }
  // a random galaxy / system / world, different for each player
  return { g: seedHash(ps ^ 0x1a2b) % 4_000_000, s: seedHash(ps ^ 0x2c3d) % 400, b: seedHash(ps ^ 0x3e4f) % 6, ps };
}
const SPAWN = querySpawn();
const TARGET = { g: SPAWN.g, s: SPAWN.s, b: SPAWN.b };
const SHIP_SEED = seedHash(SPAWN.ps ^ 0x5151) | 0;

// HUD rendering lives in ui.ts (setHud / bootDone).

async function detectWebGPU(): Promise<boolean> {
  const gpu = (navigator as unknown as { gpu?: { requestAdapter(): Promise<unknown> } }).gpu;
  if (!gpu) return false;
  try { return !!(await gpu.requestAdapter()); } catch { return false; }
}

function queryPhase(): string {
  const p = new URLSearchParams(location.search).get('phase');
  return p === 'orbit' || p === 'approach' || p === 'surface' || p === 'play' || p === 'ship' || p === 'fly' ? p : 'cruise';
}

async function main(): Promise<void> {
  const webgpu = await detectWebGPU();

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x04050a);

  // Near/far span a whole star system; a log depth buffer keeps it z-fight-free.
  const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 1, 1_000_000);
  const renderer = new THREE.WebGLRenderer({ antialias: true, logarithmicDepthBuffer: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5)); // headroom for bloom on mobile
  renderer.setSize(window.innerWidth, window.innerHeight);
  document.body.appendChild(renderer.domElement);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.2;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  const webgl2 = renderer.capabilities.isWebGL2;

  // Post-processing: bloom for cinematic glow on emissives, the sun, atmosphere.
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  const bloom = new UnrealBloomPass(
    new THREE.Vector2(window.innerWidth, window.innerHeight), 0.5, 0.5, 0.85);
  composer.addPass(bloom);
  // Cinematic colour grade: lift saturation + gentle contrast + warm bias.
  composer.addPass(new ShaderPass({
    uniforms: { tDiffuse: { value: null }, uSat: { value: 1.24 }, uCon: { value: 1.06 } },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix*modelViewMatrix*vec4(position,1.0); }',
    fragmentShader: `uniform sampler2D tDiffuse; uniform float uSat; uniform float uCon; varying vec2 vUv;
      void main(){
        vec3 c = texture2D(tDiffuse, vUv).rgb;
        float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
        c = mix(vec3(l), c, uSat);
        c = (c - 0.5) * uCon + 0.5;
        c *= vec3(1.03, 1.0, 0.965);
        gl_FragColor = vec4(clamp(c, 0.0, 1.0), 1.0);
      }`,
  }));
  composer.setSize(window.innerWidth, window.innerHeight);

  // Cinematic lighting tuned for ACES tone mapping.
  scene.add(new THREE.AmbientLight(0x2a3b55, 0.78));
  scene.add(new THREE.HemisphereLight(0xcfe2ff, 0x5a4a32, 1.9));
  const sunDir = new THREE.Vector3(1.0, 0.6, 0.85).normalize();
  const sun = new THREE.DirectionalLight(0xfff1dd, 3.8);
  sun.position.copy(sunDir).multiplyScalar(6000);
  scene.add(sun);

  // Generated nebula skybox (GPT-Image-2) as an equirectangular background.
  const nebulaTex = new THREE.TextureLoader().load('./tex/nebula.jpg');
  nebulaTex.mapping = THREE.EquirectangularReflectionMapping;
  nebulaTex.colorSpace = THREE.SRGBColorSpace;
  scene.background = nebulaTex;
  scene.environment = nebulaTex; // PBR image-based lighting — metal reflects the cosmos

  // World-gen + the detailed home world (rebuilt on every hyperdrive jump).
  const wg = await loadWorldgen().catch((e) => { console.error('worldgen load failed', e); return null; });
  const hashHex = wg
    ? BigInt.asUintN(64, wg.sw_planet_hash(UNIVERSE, TARGET.g, TARGET.s, TARGET.b)).toString(16).toUpperCase()
    : '';

  const R = 180;
  const texLoader = new THREE.TextureLoader();
  const loadTex = (u: string) => { const t = texLoader.load(u); t.colorSpace = THREE.SRGBColorSpace; return t; };
  const tex: WorldTextures = {
    rock: loadTex('./tex/rock.jpg'), grass: loadTex('./tex/grass.jpg'),
    sand: loadTex('./tex/sand.jpg'), lava: loadTex('./tex/lava.jpg'),
  };
  const anchor = new THREE.Vector3(0.32, 0.72, 0.55).normalize();
  let world = buildWorld(scene, wg, UNIVERSE, TARGET.g, TARGET.s, TARGET.b, R, tex, sunDir, anchor);

  // M1: connect to the authoritative region; render peers + beacons on-surface.
  const regionUrl = new URLSearchParams(location.search).get('region')
    || 'ws://127.0.0.1:8787/region/alpha/ws';
  const avatarTex = loadTex('./tex/avatar.png');
  const mp = createMultiplayer(scene, () => world.planet, anchor, regionUrl, avatarTex);

  // The player's procedurally-assembled modular starting ship (seeded per player).
  const hullTex = loadTex('./tex/hull.jpg');
  const ship = assembleShip(SHIP_SEED, hullTex);
  ship.group.position.set(R * 2.6, R * 0.4, -R * 2.2);
  ship.group.scale.setScalar(1.3);
  scene.add(ship.group);
  // Studio lighting for the ship showcase (key + cool rim) so the hull reads.
  const shipKey = new THREE.PointLight(0xffffff, 3.0, 600);
  shipKey.position.copy(ship.group.position).add(new THREE.Vector3(28, 42, 44));
  scene.add(shipKey);
  const shipRim = new THREE.PointLight(0x6fbaff, 1.7, 600);
  shipRim.position.copy(ship.group.position).add(new THREE.Vector3(-44, 8, -34));
  scene.add(shipRim);
  // A key light that travels with the ship, so it stays lit out in deep space.
  const travelLight = new THREE.PointLight(0xcfe6ff, 2.4, 500);
  travelLight.position.set(6, 16, 10);
  ship.group.add(travelLight);

  // ONE real planet you fly around — no scattered distant spheres (those read as
  // flat "floating circles"). Warp regenerates it into a fresh unique world.
  const homeBody: Body = { name: world.name, obj: world.planet.group, pos: new THREE.Vector3(0, 0, 0), radius: R };
  let navBodies: Body[] = [homeBody];
  const onWarp = (index: number): Body[] => {
    audio.onWarp();
    disposeWorld(scene, world);
    const a = index * 2.39996; // golden-angle spin of the star direction
    const sd = new THREE.Vector3(Math.cos(a) * 0.9, 0.55, Math.sin(a) * 0.9).normalize();
    sun.position.copy(sd).multiplyScalar(6000);
    const nb = seedHash(SPAWN.ps ^ (index * 0x9e37)) % 6;
    world = buildWorld(scene, wg, UNIVERSE, SPAWN.g, SPAWN.s + index, nb, R, tex, sd, anchor);
    homeBody.name = world.name; homeBody.obj = world.planet.group;
    navBodies = [homeBody];
    return navBodies;
  };
  const flight = createFlight(scene, ship, navBodies, R, onWarp);
  const discovery = createDiscovery();
  juice.init(scene, camera);

  // Bridge.
  let roundtrip = false;
  onNativeMessage((m) => {
    if (m.type === 'ping') { roundtrip = true; sendToNative({ type: 'pong', t: m.t }); }
  });
  sendToNative({ type: 'client-ready', webgpu, webgl2, planet: world.label, hash: hashHex });

  // Camera placement.
  const phaseMode = queryPhase();
  function place(mode: string): string {
    if (mode === 'surface') {
      const up = anchor.clone();
      const fwd = new THREE.Vector3().crossVectors(up, new THREE.Vector3(0, 1, 0)).normalize();
      const eye = world.planet.surfacePoint(anchor).addScaledVector(up, R * 0.06);
      const targetDir = anchor.clone().addScaledVector(fwd, 0.2).normalize();
      camera.up.copy(up);
      camera.position.copy(eye);
      camera.lookAt(world.planet.surfacePoint(targetDir));
      return 'surface';
    }
    if (mode === 'approach') {
      camera.position.copy(anchor).multiplyScalar(R * 1.6);
      camera.lookAt(0, 0, 0);
      return 'approach';
    }
    if (mode === 'play') {
      camera.up.set(0, 1, 0);
      camera.position.copy(anchor).multiplyScalar(R * 1.5);
      camera.lookAt(world.planet.surfacePoint(anchor));
      return 'play';
    }
    if (mode === 'ship') {
      camera.up.set(0, 1, 0);
      const c = ship.group.position;
      camera.position.set(c.x + 15, c.y + 5.5, c.z + 17);
      camera.lookAt(c.x - 1, c.y + 1.5, c.z);
      return 'ship';
    }
    if (mode === 'cruise') {
      camera.up.set(0, 1, 0);
      return 'cruise'; // camera driven each frame by the flight rig
    }
    camera.position.set(R * 2.4, R * 1.1, R * 2.4);
    camera.lookAt(0, 0, 0);
    return 'orbit';
  }

  // Touch: drag to rotate the planet.
  let dragging = false, lastX = 0;
  renderer.domElement.addEventListener('pointerdown', (e) => { dragging = true; lastX = e.clientX; });
  renderer.domElement.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    world.planet.group.rotation.y += (e.clientX - lastX) * 0.005;
    lastX = e.clientX;
  });
  renderer.domElement.addEventListener('pointerup', () => (dragging = false));

  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
    composer.setSize(window.innerWidth, window.innerHeight);
  });

  // Render loop.
  let frames = 0, last = performance.now(), fps = 0, t = 0, lastT = performance.now();
  let lastJumpCount = 0, lastDiscovered = 0;
  let phaseLabel = place(phaseMode === 'fly' ? 'orbit' : phaseMode);
  const tick = () => {
    requestAnimationFrame(tick);
    const now = performance.now();
    if (phaseMode === 'fly') {
      t += 0.0016;
      const u = (Math.sin(t) * 0.5 + 0.5); // 0..1
      const dist = THREE.MathUtils.lerp(R * 3.0, R * 1.04, u);
      const a = t * 0.25;
      camera.position.set(Math.cos(a) * dist, R * 0.5 + Math.sin(a * 0.7) * R * 0.4, Math.sin(a) * dist);
      camera.lookAt(0, 0, 0);
      phaseLabel = u > 0.85 ? 'descent' : u < 0.2 ? 'orbit' : 'approach';
    }
    mp.tick(now);
    const dt = Math.min(0.05, (now - lastT) / 1000); lastT = now;
    juice.update(dt);
    if (phaseMode === 'ship') ship.group.rotation.y += 0.006;
    let flightInfo: { speedKms: number; nearest: string; distKm: number; system: number; throttle: number; grounded: boolean } | null = null;
    if (phaseMode === 'cruise') flightInfo = flight.update(now, dt, camera);
    if (flightInfo) {
      audio.setVelocity(flightInfo.speedKms);
      if (flightInfo.system !== lastJumpCount) { lastJumpCount = flightInfo.system; juice.triggerWarp(); }
      discovery.update(flightInfo, world);
      const dc = discovery.getState().discovered.length;
      if (dc > lastDiscovered) { lastDiscovered = dc; audio.onScan(); }
    }
    for (const m of world.animated) (m.material as THREE.ShaderMaterial).uniforms.uTime.value = now * 0.001;
    world.planet.update(camera.position);
    composer.render();
    frames++;
    if (now - last >= 500) {
      fps = Math.round((frames * 1000) / (now - last));
      frames = 0; last = now;
      const altKm = Math.round((camera.position.length() - R) * (world.radiusKm / R));
      const ms = mp.stats();
      if (phaseMode === 'cruise') {
        const jumps = flightInfo?.system ?? 0;
        setHud({
          sub: `${world.label}   ·   ${jumps > 0 ? 'uncharted' : 'home system'}`,
          title: 'In Flight',
          rows: [
            ['Speed', `${(flightInfo?.speedKms ?? 0).toLocaleString()} km/s`],
            ['Altitude', flightInfo?.grounded ? 'LANDED' : `${(flightInfo?.distKm ?? 0).toLocaleString()} km`],
            ['Thrust', `${Math.round((flightInfo?.throttle ?? 0) * 100)}%`],
            ...discovery.getHudRows().slice(0, 2),
          ],
          bottom: `${ms.connected || roundtrip || bridgeAvailable() ? 'Linked' : 'Solo'}   ·   ${fps} fps`,
        });
      } else if (phaseMode === 'ship') {
        const s = ship.stats;
        setHud({
          sub: `Galaxy ${SPAWN.g} · System ${SPAWN.s} · World ${SPAWN.b}`,
          title: 'Starship',
          rows: [
            ['Frame', ship.parts.map((p) => p.id.replace(/^[^-]+-/, '')).join(' · ')],
            ['Thrust', `${s.thrust}`],
            ['Mass', `${s.mass}`],
            ['Cargo', `${s.cargo}`],
            ['Shield', `${s.shield}`],
            ['Agility', `${s.agility}`],
          ],
          bottom: `Ship #${(ship.seed >>> 0).toString(16)}   ·   ${fps} fps`,
        });
      } else {
        setHud({
          sub: `${world.label}   ·   G${SPAWN.g} · S${SPAWN.s} · B${SPAWN.b}`,
          title: phaseLabel.charAt(0).toUpperCase() + phaseLabel.slice(1),
          rows: [
            ['Altitude', `${altKm} km`],
            ['Terrain', `${world.planet.leafCount}`],
            ['Explorers', `${ms.players + 1}`],
            ['Beacons', `${ms.owned} / ${ms.total}`],
          ],
          bottom: `${ms.connected || roundtrip || bridgeAvailable() ? 'Linked' : 'Solo'}   ·   ${fps} fps`,
        });
      }
    }
  };
  // Procedural soundscape — starts on first interaction (autoplay policy).
  window.addEventListener('pointerdown', () => audio.init(), { once: true });
  bootDone();
  initOnboarding();
  tick();
}

main().catch((e) => { bootDone(); setHud({ sub: 'Initialisation error', bottom: String(e) }); console.error(e); });
